import dayjs from 'dayjs';
import React from 'react';
import { capitalizeFirstLetter, formatDate } from 'utils/utils';

interface DateDisplayProps {
	date: string | Date;
	dateFormat?: string;
	showDayName?: boolean;
	compact?: boolean;
	className?: string;
	style?: React.CSSProperties;
	isHoliday?: boolean;
	highlightSpecialDays?: boolean;
	isAbsent?:boolean;
}

export const DateDisplay: React.FC<DateDisplayProps> = ({
	date,
	dateFormat = 'DD/MM/YYYY',
	showDayName = true,
	compact = false,
	className = '',
	style = {},
	isHoliday = false,
	isAbsent =false,
	highlightSpecialDays = false,
}) => {
	// Helper function to get day name (3 letter abbreviation)
	const getDayName = (dateValue: string | Date): string => {
		const dateObj = new Date(dateValue);
		const dayNames = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
		return dayNames[dateObj.getDay()];
	};

	const isSunday = dayjs(date).day() === 0;

	const getColors = () => {
		let dateColor = 'inherit'; 
		let dayColor = '#584495'; 

		if (highlightSpecialDays) {
			if (isHoliday) {
				dateColor = '#389e0d';
				dayColor = '#389e0d'; 
			} else if (isSunday) {
				dateColor = '#1976d2'; 
				dayColor = '#1976d2'; 
			}
			else if(isAbsent){
					dateColor = '#d32f2f';
					dayColor = '#d32f2f'; 
			}
		}

		return { dateColor, dayColor };
	};

	const { dateColor, dayColor } = getColors();

	const getDayNameWithStyle = () => {
		const dayName = capitalizeFirstLetter(getDayName(date));

		return (
			<span style={{ color: dayColor, marginLeft: '5px' }}>
				({dayName})
			</span>
		);
	};

	// Format the date using your existing utility
	const formattedDate = formatDate(new Date(date), dateFormat);

	if (!date) {
		return (
			<span className={className} style={style}>
				-
			</span>
		);
	}

	const combinedStyle = {
		...style,
		color: dateColor,
		fontWeight:
			highlightSpecialDays && (isHoliday || isSunday ||isAbsent) ? '500' : 'normal',
	};

	return (
		<span className={`date-display ${className}`} style={combinedStyle}>
			{showDayName ? (
				<>
					{formattedDate}
					{getDayNameWithStyle()}
				</>
			) : (
				formattedDate
			)}
		</span>
	);
};

export const DateDisplayAlternative: React.FC<DateDisplayProps> = ({
	date,
	dateFormat = 'DD/MM/YYYY',
	showDayName = true,
	compact = false,
	className = '',
	style = {},
	isHoliday = false,
	highlightSpecialDays = false,
	isAbsent=false
}) => {
	const getDayName = (dateValue: string | Date): string => {
		const dateObj = new Date(dateValue);
		const dayNames = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
		return dayNames[dateObj.getDay()];
	};

	const isSunday = dayjs(date).day() === 0;
	const formattedDate = formatDate(new Date(date), dateFormat);

	const getDateStyle = () => {
		let dateStyle: React.CSSProperties = {};

		if (highlightSpecialDays) {
			if (isHoliday) {
				dateStyle = { color: '#389e0d', fontWeight: '500' };
			} else if (isSunday) {
				dateStyle = { color: '#1976d2', fontWeight: '500' };
			}
			else if(isAbsent){
								dateStyle = {
									color: '#d32f2f',
									fontWeight: '500',
								};

			}
		}

		return dateStyle;
	};

	const getDayStyle = () => {
		let dayStyle: React.CSSProperties = {
			color: '#584495',
			marginLeft: '5px',
		};

		if (highlightSpecialDays) {
			if (isHoliday) {
				dayStyle = { ...dayStyle, color: '#389e0d', fontWeight: '500' };
			} else if (isSunday) {
				dayStyle = { ...dayStyle, color: '#1976d2', fontWeight: '500' };
			} else if (isAbsent) {
				dayStyle = {
					...dayStyle,
					color: '#d32f2f',
					fontWeight: '500',
				};
			}
		}

		return dayStyle;
	};

	if (!date) {
		return (
			<span className={className} style={style}>
				-
			</span>
		);
	}

	return (
		<span className={`date-display ${className}`} style={style}>
			{showDayName ? (
				<>
					<span style={getDateStyle()}>{formattedDate}</span>
					<span style={getDayStyle()}>
						({capitalizeFirstLetter(getDayName(date))})
					</span>
				</>
			) : (
				<span style={getDateStyle()}>{formattedDate}</span>
			)}
		</span>
	);
};
